import logo from '../assets/logo.png';
import React, { useEffect } from 'react';
import { Container, Typography, Box, Link as MuiLink, Button, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const LandingPage = () => {
  const theme = useTheme();

  useEffect(() => {
    document.getElementById("year").textContent = new Date().getFullYear();
  }, []);

  const styles = {
    body: {
      margin: 0,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '2rem',
      transition: 'background-color 0.3s ease, color 0.3s ease', // Surprise animation
    },
    header: {
      textAlign: 'center',
      marginBottom: '2rem',
    },
    headerImg: {
      maxWidth: '160px',
      height: 'auto',
    },
    h1: {
      fontSize: '2rem',
      margin: '1rem 0 0.5rem',
      fontWeight: 700,
    },
    p: {
      maxWidth: '650px',
      fontSize: '1.1rem',
      lineHeight: 1.6,
      margin: '0 auto 1rem',
      textAlign: 'left',
    },
    ul: {
      maxWidth: '650px',
      margin: '0 auto 1.5rem',
      paddingLeft: '2.2rem',
      textAlign: 'left',
      listStylePosition: 'outside',
    },
    li: {
      marginBottom: '0.6rem',
      lineHeight: 1.6,
    },
    legalLinks: {
      display: 'flex',
      gap: '1.5rem',
      justifyContent: 'center',
      marginTop: '2rem',
    },
    legalLink: {
      padding: '0.4rem 1rem',
      fontSize: '0.95rem',
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '999px',
      textDecoration: 'none',
      fontWeight: 500,
      transition: 'all 0.3s ease',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: '0 3px 8px rgba(0, 0, 0, 0.08)',
      },
    },
    footer: {
      marginTop: '3rem',
      fontSize: '0.9rem',
      color: theme.palette.text.secondary,
    },
    hr: {
      border: 'none',
      borderTop: `2px solid ${theme.palette.divider}`,
      margin: '2rem auto',
      maxWidth: '100px',
    },
  };

  return (
    <Box sx={styles.body}>
      <header style={styles.header}>
        <img src="https://imagizer.imageshack.com/v2/280x200q70/924/1yx2xL.png" alt="gdscreate automations" style={styles.headerImg} />
        <Typography variant="h1" component="h1" sx={styles.h1}>
          gdscreate Automations
        </Typography>
      </header>

      <main>
        <Container maxWidth="sm">
          <Typography variant="body1" sx={styles.p}>
            Our automation system is deeply integrated with Google Drive and operates on behalf of authenticated users. To enable these automations, you securely connect your Google account via OAuth.
          </Typography>

          <Typography variant="body1" sx={styles.p}>
            This application does not include a traditional user interface — it works entirely through seamless integrations.
          </Typography>

          <Divider sx={styles.hr} />

          <Typography variant="body1" sx={styles.p}>
            To function, the app requests specific Google permissions that allow it to:
          </Typography>

          <ul style={styles.ul}>
            <li style={styles.li}>Create files in your own Google Drive.</li>
            <li style={styles.li}>Write automation results into these files.</li>
            <li style={styles.li}>Request input data within the files it creates.</li>
          </ul>

          <Typography variant="body1" sx={styles.p}>
            For your privacy and security, the app only reads and writes data from its own created files. You can review the exact permission scopes in the privacy policy.
          </Typography>

          <Divider sx={styles.hr} />

          <Typography variant="body1" sx={styles.p}>
            If at any point you wish to revoke the app’s access to your Google Drive, you can:
          </Typography>

          <ul style={styles.ul}>
            <li style={styles.li}>Go to <MuiLink href="https://myaccount.google.com/permissions" target="_blank" rel="noopener">Google Account Permissions</MuiLink>.</li>
            <li style={styles.li}>Sign in with the Google account you used to connect the app.</li>
            <li style={styles.li}>Locate this application in the list of third-party apps.</li>
            <li style={styles.li}>Select it and click <strong>Remove Access</strong>.</li>
          </ul>

          <Typography variant="body1" sx={styles.p}>
            Once removed, the app will no longer be able to access your Google Drive or any files it previously created.
          </Typography>

          <Box sx={{ ...styles.legalLinks, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <Button component={RouterLink} to="/automations" variant="contained" sx={{ ...styles.legalLink, backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': { backgroundColor: theme.palette.primary.dark } }}>
              Explore our Automations
            </Button>
            <Button component={RouterLink} to="/privacy-policy" variant="outlined" sx={styles.legalLink}>
              Privacy Policy
            </Button>
            <Button component={RouterLink} to="/terms-of-service" variant="outlined" sx={styles.legalLink}>
              Terms of Service
            </Button>
          </Box>
        </Container>
      </main>

      <footer style={styles.footer}>
        &copy; <span id="year"></span> gdscreate. All rights reserved.
      </footer>
    </Box>
  );
};

export default LandingPage;
